import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock, Plus, Trash2, CalendarDays, User } from 'lucide-react';

const LEAVE_TYPE_LABELS = {
  vacation: 'Vacaciones', sick: 'Enfermedad', personal: 'Personal',
  maternity: 'Maternidad', paternity: 'Paternidad', bereavement: 'Duelo/Luto', other: 'Otro',
};

const STATUS_CONFIG = {
  pending:  { label: 'Pendiente', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  approved: { label: 'Aprobada',  color: 'bg-green-100 text-green-800',  icon: CheckCircle },
  rejected: { label: 'Rechazada', color: 'bg-red-100 text-red-800',      icon: XCircle },
};

export default function LeaveRequestsTab({ role = 'admin' }) {
  const { user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [reviewModal, setReviewModal] = useState(null); // { request }
  const [adminNotes, setAdminNotes] = useState('');
  const [form, setForm] = useState({
    leave_type: 'vacation', start_date: '', end_date: '', days_requested: 1, reason: '',
  });

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const params = filterStatus !== 'all' ? { status: filterStatus } : {};
      const res = await axios.get('/leave-requests', { params });
      setRequests(res.data);
    } catch { toast.error('Error al cargar solicitudes'); }
    finally { setLoading(false); }
  }, [filterStatus]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const fetchBalance = useCallback(async () => {
    if (role !== 'teacher' || !user?.user_id) return;
    try {
      const res = await axios.get(`/hr/employees/${user.user_id}/leave-balance`);
      setBalance(res.data);
    } catch { setBalance(null); }
  }, [role, user]);

  useEffect(() => { fetchBalance(); }, [fetchBalance, requests]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.start_date || !form.end_date) { toast.error('Selecciona las fechas'); return; }
    try {
      await axios.post('/leave-requests', form);
      toast.success('Solicitud enviada exitosamente');
      setShowForm(false);
      setForm({ leave_type: 'vacation', start_date: '', end_date: '', days_requested: 1, reason: '' });
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al enviar'); }
  };

  const handleReview = async (status) => {
    try {
      await axios.put(`/leave-requests/${reviewModal.id}/review`, { status, admin_notes: adminNotes });
      toast.success(status === 'approved' ? 'Solicitud aprobada' : 'Solicitud rechazada');
      setReviewModal(null);
      setAdminNotes('');
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Cancelar esta solicitud?')) return;
    try {
      await axios.delete(`/leave-requests/${id}`);
      toast.success('Solicitud cancelada');
      fetchRequests();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const calcDays = (start, end) => {
    if (!start || !end) return 1;
    const diff = (new Date(end) - new Date(start)) / (1000 * 60 * 60 * 24);
    return Math.max(1, Math.round(diff) + 1);
  };

  const filtered = filterStatus === 'all' ? requests : requests.filter(r => r.status === filterStatus);
  const pending = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Solicitudes de Licencias</h2>
          {pending > 0 && <p className="text-sm text-yellow-600 font-medium">{pending} solicitud(es) pendiente(s)</p>}
        </div>
        <Button onClick={() => setShowForm(!showForm)} data-testid="new-leave-request-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white">
          <Plus className="w-4 h-4 mr-1" /> Nueva Solicitud
        </Button>
      </div>

      {/* Balance del empleado (solo profesor) */}
      {role === 'teacher' && balance && (
        <div className="grid grid-cols-2 gap-3" data-testid="teacher-leave-balance">
          <Card className="border-green-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-green-700 mb-2">🏖️ Vacaciones disponibles</p>
              <p className="text-3xl font-bold text-green-600" data-testid="teacher-vacation-available">{balance.vacation.available.toFixed(1)} <span className="text-sm font-normal text-slate-400">días</span></p>
              <p className="text-[11px] text-slate-500 mt-1">Acumulados {balance.vacation.accrued.toFixed(1)} · Usados {balance.vacation.used.toFixed(1)}</p>
            </CardContent>
          </Card>
          <Card className="border-orange-200">
            <CardContent className="p-4">
              <p className="text-xs font-semibold text-orange-700 mb-2">🤒 Enfermedad disponibles</p>
              <p className="text-3xl font-bold text-orange-600" data-testid="teacher-sick-available">{balance.sick.available.toFixed(1)} <span className="text-sm font-normal text-slate-400">días</span></p>
              <p className="text-[11px] text-slate-500 mt-1">Acumulados {balance.sick.accrued.toFixed(1)} · Usados {balance.sick.used.toFixed(1)}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* New Request Form */}
      {showForm && (
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader><CardTitle className="text-base text-blue-800">Nueva Solicitud de Licencia</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-gray-600">Tipo de licencia</label>
                <select data-testid="leave-type-select" value={form.leave_type}
                  onChange={e => setForm({ ...form, leave_type: e.target.value })}
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white">
                  {Object.entries(LEAVE_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Días solicitados</label>
                <input type="number" min="1" max="90" value={form.days_requested} data-testid="leave-days-input"
                  onChange={e => setForm({ ...form, days_requested: parseInt(e.target.value) || 1 })}
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Fecha inicio</label>
                <input type="date" value={form.start_date} data-testid="leave-start-date"
                  onChange={e => {
                    const s = e.target.value;
                    setForm(f => ({ ...f, start_date: s, days_requested: calcDays(s, f.end_date) }));
                  }}
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm" required />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600">Fecha fin</label>
                <input type="date" value={form.end_date} data-testid="leave-end-date"
                  min={form.start_date}
                  onChange={e => {
                    const en = e.target.value;
                    setForm(f => ({ ...f, end_date: en, days_requested: calcDays(f.start_date, en) }));
                  }}
                  className="w-full mt-1 border rounded-md px-3 py-2 text-sm" required />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-gray-600">Razón (opcional)</label>
                <textarea value={form.reason} onChange={e => setForm({ ...form, reason: e.target.value })}
                  rows={2} className="w-full mt-1 border rounded-md px-3 py-2 text-sm" data-testid="leave-reason" />
              </div>
              <div className="sm:col-span-2 flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
                <Button type="submit" data-testid="submit-leave-btn" className="bg-blue-600 text-white">Enviar Solicitud</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap">
        {['all', 'pending', 'approved', 'rejected'].map(s => (
          <button key={s} onClick={() => setFilterStatus(s)} data-testid={`filter-${s}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${filterStatus === s ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
            {s === 'all' ? 'Todas' : STATUS_CONFIG[s]?.label}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center text-gray-400 py-8">Cargando...</div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <CalendarDays className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p>No hay solicitudes</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map(req => {
            const sc = STATUS_CONFIG[req.status] || STATUS_CONFIG.pending;
            const Icon = sc.icon;
            return (
              <Card key={req.id} className="border-gray-200" data-testid={`leave-request-${req.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-800 text-sm">
                          {LEAVE_TYPE_LABELS[req.leave_type] || req.leave_type}
                        </span>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${sc.color}`}>
                          <Icon className="w-3 h-3" />{sc.label}
                        </span>
                      </div>
                      {role === 'admin' && (
                        <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                          <User className="w-3 h-3" />{req.employee_name}
                        </div>
                      )}
                      <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
                        <CalendarDays className="w-3 h-3" />
                        {req.start_date} → {req.end_date} · {req.days_requested} día(s)
                      </div>
                      {req.reason && <p className="text-xs text-gray-500 mt-1">"{req.reason}"</p>}
                      {req.admin_notes && (
                        <p className="text-xs text-blue-600 mt-1"><strong>Nota admin:</strong> {req.admin_notes}</p>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {role === 'admin' && req.status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => { setReviewModal(req); setAdminNotes(''); }}
                          data-testid={`review-leave-${req.id}`} className="text-blue-600">
                          Revisar
                        </Button>
                      )}
                      {req.status === 'pending' && (
                        <Button size="sm" variant="outline" onClick={() => handleDelete(req.id)}
                          data-testid={`cancel-leave-${req.id}`} className="text-red-500">
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="font-bold text-gray-900 mb-1">Revisar Solicitud</h3>
            <p className="text-sm text-gray-500 mb-4">
              {reviewModal.employee_name} · {LEAVE_TYPE_LABELS[reviewModal.leave_type]} · {reviewModal.days_requested} día(s)
            </p>
            <div className="mb-4">
              <label className="text-xs font-medium text-gray-600">Notas (opcional)</label>
              <textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} rows={3}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm" data-testid="admin-notes-input"
                placeholder="Comentario para el empleado..." />
            </div>
            <div className="flex gap-3">
              <Button onClick={() => handleReview('approved')} data-testid="approve-leave-btn"
                className="flex-1 bg-green-600 hover:bg-green-700 text-white">
                <CheckCircle className="w-4 h-4 mr-1" /> Aprobar
              </Button>
              <Button onClick={() => handleReview('rejected')} data-testid="reject-leave-btn"
                className="flex-1 bg-red-600 hover:bg-red-700 text-white">
                <XCircle className="w-4 h-4 mr-1" /> Rechazar
              </Button>
              <Button variant="outline" onClick={() => setReviewModal(null)}>Cancelar</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
